import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  ArrowRight,
  CheckCircle,
  BookOpen,
  TrendingUp,
  Users,
  BarChart3,
  Clock,
  Brain,
  Target,
  Eye,
  Play,
  Award,
  Video,
  MessageSquare,
  Trophy,
  Monitor,
  Smartphone,
  Lightbulb,
  Shield,
  UserPlus,
  Sparkles,
  ClipboardCheck,
  Building2,
  RefreshCw
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { learningManagementSystemTestimonials } from '../../shared/testimonialData';

const LearningManagementSystem: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);


  const features = [
    {
      icon: BookOpen,
      title: 'Multi-Campus, Multi-Tenant Architecture',
      description: 'Onboard entire universities, colleges, and training centers into a single platform while keeping data, branding, and workflows isolated for each institution.'
    },
    {
      icon: Users,
      title: 'AI Tutors & Adaptive Learning Journeys',
      description: 'Always-on AI tutors assist learners with concept explanations, practice questions, and personalized recommendations based on performance and learning patterns.'
    },
    {
      icon: Video,
      title: 'Auto-Generated Flashcards & Aptitude Drillst',
      description: 'Converts course content into bite-sized flashcards and targeted aptitude drills that keep learners engaged and exam-ready without extra effort from faculty.'
    },
    {
      icon: BarChart3,
      title: 'Integrated Fee Billing & Finance Workflows',
      description: 'Built-in fee plans, automated invoices, reminders, and digital payment options reduce queues, paperwork, and reconciliation overhead for accounts teams'
    },
    {
      icon: Trophy,
      title: 'Course Authoring & AI Content Enrichment',
      description: 'Faculty can quickly create courses, upload materials, and use AI to enrich content with quizzes, summaries, and assessments aligned to learning outcomes.'
    },
    {
      icon: MessageSquare,
      title: 'Attendance, Assessment & Gradebook',
      description: 'Digital attendance capture, online and offline assessments, and central gradebooks give administrators a live view of academic progress.'
    },
    {
      icon: Shield,
      title: 'Analytics Dashboards & Outcome Reports',
      description: 'Real-time dashboards for management, department heads, and faculty, showing enrolments, engagement levels, completion rates, and fee status.'
    },
    {
      icon: Smartphone,
      title: 'Granular Role-Based Access Control (RBAC)',
      description: 'Fine-grained permissions for administrators, faculty, non-teaching staff, students, and parents ensure that each stakeholder sees exactly what they should—and nothing more.'
    },
    {
      icon: Lightbulb,
      title: 'Mobile-First Experience',
      description: 'Native-like responsive interfaces for smartphones and tablets so learners can study, pay fees, and track progress from anywhere.'
    }
  ];

  const solutions = [
    {
      icon: Monitor,
      title: 'Universities & Multi-Campus Institutions',
      description: 'Standardise teaching, assessment, and reporting across multiple campuses while allowing each college or department to operate with its own rules and branding.',
      features: ['Employee Onboarding', 'Skill Assessment', 'Compliance Tracking', 'Performance Analytics', 'Custom Workflows']
    },
    {
      icon: GraduationCap,
      title: 'Engineering, Medical & Professional Colleges',
      description: 'Manage complex curriculam, lab courses, internships, and exam schedules with AI-enhanced study aids that help students handle demanding programs.',
      features: ['Course Catalog', 'Grade Management', 'Student Enrollment', 'Academic Calendar', 'Parent Portals']
    },
    {
      icon: Brain,
      title: 'Coaching Centers & Test Prep Institutes',
      description: 'Deliver structured test prep with auto-generated practice sets, mock tests, and personalized performance analytics for learners.',
      features: ['Course Marketplace', 'Payment Integration', 'Multi-language Support', 'Mobile Learning', 'Social Learning']
    },
    {
      icon: Target,
      title: 'Corporate L&D & Training Academies',
      description: 'Onboard employees, deliver skill programs, and certify learning with clear visibility into who has completed what—and how it impacted performance.',
      features: ['Proctored Exams', 'CEU Tracking', 'Industry Standards', 'Verification System', 'Renewal Management']
    },
    {
      icon: Target,
      title: 'Online & Hybrid Education Providers',
      description: 'Run fully online or blended courses with seamless access to content, assessments, live classes, and support, all tracked in a single system.',
      features: ['Proctored Exams', 'CEU Tracking', 'Industry Standards', 'Verification System', 'Renewal Management']
    },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Higher Student Success & Engagement',
      description: 'AI tutors, personalized drills, and mobile-first access help students stay on track, close learning gaps quickly, and perform better in assessments',
      metric: '85% better retention'
    },
    {
      icon: Clock,
      title: 'Operational Efficiency for Admin & Finance',
      description: 'Automated attendance, assessments, fee reminders, and reconciliation reduce manual workloads across academic and non-academic teams.',
      metric: '60% faster completion'
    },
    {
      icon: Users,
      title: 'Consistent Academic Quality Across Campuses',
      description: 'Shared content libraries, standard templates, and common reporting frameworks help maintain quality while scaling.',
      metric: '10x student capacity'
    },
    {
      icon: Award,
      title: 'Better Decision-Making for Leadership',
      description: 'Institution-wide dashboards turn fragmented data into actionable insights on enrolments, fees, pass rates, and resource utilization.',
      metric: '70% cost reduction'
    },
    {
      icon: Eye,
      title: 'Improved Parent & Stakeholder Visibility',
      description: 'Optional parent access and stakeholder reports build trust and accountability around academic progress and fee status.',
    }
  ];

  const integrations = [
    {
      category: 'Video Platforms',
      tools: ['Zoom', 'YouTube', 'Vimeo', 'WebEx', 'Microsoft Teams', 'Google Meet']
    },
    {
      category: 'Content Tools',
      tools: ['Google Workspace', 'Office 365', 'Canva', 'Adobe Creative', 'H5P', 'Articulate']
    },
    {
      category: 'Assessment Tools',
      tools: ['ProctorU', 'Respondus', 'ExamSoft', 'Turnitin', 'Gradescope', 'Custom Quizzes']
    },
    {
      category: 'Analytics & CRM',
      tools: ['Google Analytics', 'Salesforce', 'HubSpot', 'Tableau', 'Power BI', 'Custom APIs']
    }
  ];

  const security = [
    {
      title: 'Isolated Tenants per Institution',
      description: 'Each college or organization has its own logically isolated environment, ensuring strict separation of data, configurations, and user roles.'
    },
    {
      title: 'Granular RBAC & Access Governance',
      description: 'Admins can tightly control who accesses academic records, financial data, and analytics dashboards, aligning with institutional policies and regional regulations.'
    },
    {
      title: 'Secure Payment Handling',
      description: 'Integrations with trusted payment gateways, encryption of financial data, and auditable billing workflows help finance teams stay compliant and secure.'
    },
    {
      title: 'Encrypted Data at Rest & in Transit',
      description: 'NStrong encryption standards protect student records, assessment data, and fee transactions from end to end.'
    },
    {
      title: 'Audit Trails & Compliance Reportingt',
      description: 'Every login, configuration change, and financial transaction is logged, making internal and external audits faster and more transparent.'
    },
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-orange-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 neon-glow">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-orange-400 uppercase tracking-wide">Educational Innovation Platform</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Learning Management <span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">System</span>
                  </h1>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                The AI-powered Learning Management System (LMS) turns every campus, department, and classroom into a connected digital learning space. From course creation to certifications, it gives institutions a single platform to deliver engaging, outcome-driven education at scale.Built for rapid multi-campus rollout, the LMS offers a mobile-first experience so students, faculty, and administrators can teach, learn, and manage on the go. With integrated fee billing and AI-driven learning tools, it helps institutions boost academic performance while simplifying daily operations.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-red-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300"
                >
                  Start Learning Platform
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-500">100K+</div>
                  <div className="text-sm text-muted-foreground">Active Learners</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-400">85%</div>
                  <div className="text-sm text-muted-foreground">Completion Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">24/7</div>
                  <div className="text-sm text-muted-foreground">Availability</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-gradient-to-r from-orange-500 to-red-600 opacity-20 rounded-full blur-3xl"
                />

                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Products/ai-Products/LearningManagementSystem.png'
                    alt="Learning Management System Dashboard"
                    className="w-full h-96 object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-orange-500/20 rounded-lg backdrop-blur-sm">
                          <GraduationCap className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <p className="font-semibold">Learning-Powered</p>
                          <p className="text-sm text-white/80">Educational Platform</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-orange-500">
                    <BookOpen className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">LEARNING</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Powered LMS Platform Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Core Features
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Everything you need to create, deliver, and manage effective educational programs with advanced tracking and engagement tools.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="mb-4">
                    <Icon className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LMS Academic Flow Section */}
      <section className="relative py-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              <span className="brand-gradient-text">LMS Academic Flow</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              A comprehensive academic workflow that streamlines every stage of the educational journey from onboarding to certification.
            </p>
          </motion.div>

          {/* Flow Diagram */}
          <div className="mb-16">
            <div className="relative">
              {/* Desktop Flow */}
              <div className="hidden lg:flex items-center justify-between gap-4 mb-12">
                {[
                  { icon: BookOpen, title: 'Course Creation', desc: 'Instructor creates courses and learning materials', color: 'primary' },
                  { icon: UserPlus, title: 'Student Enrollment', desc: 'Students register and access course content', color: 'accent' },
                  { icon: Sparkles, title: 'AI Assistance', desc: 'AI tutor provides personalized guidance', color: 'secondary' },
                  { icon: ClipboardCheck, title: 'Assessment', desc: 'Quizzes and assignments with auto-grading', color: 'primary' },
                  { icon: BarChart3, title: 'Analytics', desc: 'Performance tracking and insights', color: 'accent' }
                ].map((step, index) => (
                  <React.Fragment key={step.title}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.15 }}
                      viewport={{ once: true }}
                      className="flex-1 text-center"
                    >
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        className="relative mb-6 mx-auto"
                      >
                        <div className={`w-24 h-24 rounded-full bg-gradient-to-br from-${step.color}/20 to-${step.color}/10 flex items-center justify-center glass-strong border-2 border-${step.color}/30 mx-auto`}>
                          <step.icon className={`w-10 h-10 text-${step.color}`} />
                        </div>
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className={`absolute inset-0 rounded-full bg-${step.color}/10 blur-xl`}
                        />
                      </motion.div>
                      <h4 className="font-semibold text-lg mb-2">{step.title}</h4>
                      <p className="text-sm text-muted-foreground">{step.desc}</p>
                    </motion.div>

                    {index < 4 && (
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.15 + 0.3 }}
                        viewport={{ once: true }}
                        className="flex-shrink-0"
                      >
                        <div className="relative">
                          <ArrowRight className="w-8 h-8 text-primary" />
                          <motion.div
                            animate={{ x: [0, 10, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="absolute inset-0"
                          >
                            <ArrowRight className="w-8 h-8 text-primary/30" />
                          </motion.div>
                        </div>
                      </motion.div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Mobile Flow */}
              <div className="lg:hidden space-y-6">
                {[
                  { icon: BookOpen, title: 'Course Creation', desc: 'Instructor creates courses and learning materials', color: 'primary' },
                  { icon: UserPlus, title: 'Student Enrollment', desc: 'Students register and access course content', color: 'accent' },
                  { icon: Sparkles, title: 'AI Assistance', desc: 'AI tutor provides personalized guidance', color: 'secondary' },
                  { icon: ClipboardCheck, title: 'Assessment', desc: 'Quizzes and assignments with auto-grading', color: 'primary' },
                  { icon: BarChart3, title: 'Analytics', desc: 'Performance tracking and insights', color: 'accent' }
                ].map((step, index) => (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="glass-strong p-6 rounded-xl"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-${step.color}/20 to-${step.color}/10 flex items-center justify-center flex-shrink-0 border-2 border-${step.color}/30`}>
                        <step.icon className={`w-8 h-8 text-${step.color}`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg mb-2">{step.title}</h4>
                        <p className="text-sm text-muted-foreground">{step.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Academic Sequence & Continuous Improvement */}
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="glass-strong p-8 rounded-2xl"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-primary/20">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-semibold">Academic Sequence</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                The academic flow includes tenant onboarding, course creation, AI content enrichment, attendance & assessment, analytics dashboards, and certificates. Granular RBAC isolates data per college.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="glass-strong p-8 rounded-2xl"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-accent/20">
                  <RefreshCw className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-2xl font-semibold">Continuous Improvement</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                The LMS platform continuously improves its academic capabilities, ensuring that users have access to the latest insights and tools for their education.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Learning Types Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center hidden">
              Adapt to any educational need with flexible learning management that supports corporate training, academic education, and professional certification programs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={solution.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-orange-500/20">
                      <Icon className="w-8 h-8 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{solution.title}</h3>
                      <p className="text-muted-foreground">{solution.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 hidden">
                    {solution.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-red-400" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advanced Capabilities */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Data Security & Compliance
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">

            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {security.map((security, index) => (
              <motion.div
                key={security.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <h3 className="text-xl font-semibold mb-3 text-orange-500">{security.title}</h3>
                <p className="text-muted-foreground">{security.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Platform Benefits
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Everything you need to create, deliver, and manage effective educational programs with advanced tracking and engagement tools.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="mb-4">
                    <Icon className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Educational Impact
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Transform your educational programs with quantifiable improvements in learning outcomes, efficiency, and cost-effectiveness.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-orange-500/20">
                      <Icon className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <div className="text-red-400 font-semibold">{benefit.metric}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="relative py-15 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Seamless Educational Integrations
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Connect with your existing educational tools and platforms for a unified learning ecosystem.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {integrations.map((integration, index) => (
              <motion.div
                key={integration.category}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <h3 className="text-lg font-semibold mb-4 text-orange-500">{integration.category}</h3>
                <div className="space-y-2">
                  {integration.tools.map((tool) => (
                    <div key={tool} className="text-sm text-muted-foreground">{tool}</div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 hidden">
        <ProductTestimonials
          testimonials={learningManagementSystemTestimonials}
          title="Learning Excellence Stories"
          subtitle="See how organizations transformed education with smart learning platforms"
          gradientFrom="#FF6B35"
          gradientVia="#FF4500"
          gradientTo="#FF6B35"
          iconColor="#FF6B35"
          dotColor="bg-orange-500"
          speed={65}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Transform Education with Smart Learning
            </h2>
            <p className="text-lg text-muted-foreground mb-8 text-center">
              Launch your comprehensive learning management system and revolutionize how your organization delivers education and training.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-red-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300"
              >
                Start Learning Platform
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Schedule Demo
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { LearningManagementSystem };